import { useEffect, useState } from "react";

/* A chunk the renderer imports on first use — the highlighter, KaTeX, Mermaid — so a page that
   never shows one never fetches it. A failed import (offline, a new deploy) tries again next time. */
export type Chunk<T> = { loaded: T | null; load: () => Promise<T> };

export function lazyChunk<T>(load: () => Promise<T>): Chunk<T> {
  let loading: Promise<T> | null = null;
  const chunk: Chunk<T> = {
    loaded: null,
    load: () =>
      (loading ??= load().then(
        (module) => (chunk.loaded = module),
        (error) => {
          loading = null;
          throw error;
        },
      )),
  };
  return chunk;
}

/** The chunk's module once it is here: null on a render before that. */
export function useChunk<T>(chunk: Chunk<T>): T | null {
  const [module, setModule] = useState(() => chunk.loaded);
  useEffect(() => {
    if (!module) chunk.load().then(setModule, () => undefined);
  }, [chunk, module]);
  return module;
}
